import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Clock,
  Download
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Document {
  id: string;
  filename: string;
  document_type: string;
  file_size: number;
  is_validated: boolean;
  created_at: string;
  file_url: string;
}

interface RoadmapDocumentsPanelProps {
  projectId: string;
}

const DOCUMENT_TYPE_LABELS: { [key: string]: string } = {
  BUSINESS_PLAN: 'Business Plan',
  FINANCIAL_STATEMENT: 'Plan financier',
  LEGAL_DOCUMENT: 'Document légal',
  IDENTITY: 'Pièce d\'identité',
  CERTIFICATE: 'Certificat',
  KBIS: 'KBIS',
  RIB: 'RIB',
  DESCRIPTION: 'Description',
  OTHER: 'Autre'
};

export const RoadmapDocumentsPanel: React.FC<RoadmapDocumentsPanelProps> = ({
  projectId
}) => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    validated: 0,
    pending: 0
  });

  useEffect(() => {
    fetchDocuments();
  }, [projectId]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      setDocuments(data || []);

      const validated = (data || []).filter((doc) => doc.is_validated).length;
      setStats({
        total: (data || []).length,
        validated,
        pending: (data || []).length - validated
      });
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-green-600" />
          <h3 className="font-semibold text-gray-900">Documents</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
            {stats.validated} validés
          </span>
          {stats.pending > 0 && (
            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
              {stats.pending} en attente
            </span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-sm">Aucun document pour ce projet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((document) => (
            <div
              key={document.id}
              className="p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {document.is_validated ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 text-sm truncate">
                    {document.filename}
                  </h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">
                      {DOCUMENT_TYPE_LABELS[document.document_type] || document.document_type}
                    </span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">
                      {formatFileSize(document.file_size)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                    <Clock className="w-3 h-3" />
                    {formatDistanceToNow(new Date(document.created_at), {
                      addSuffix: true,
                      locale: fr
                    })}
                  </div>
                </div>

                <a
                  href={document.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Télécharger"
                >
                  <Download className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}

          <button
            onClick={() => navigate(`/projects/${projectId}/documents`)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-green-600 hover:text-green-700 font-medium"
          >
            Voir tous les documents
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
